import { Component, computed, inject } from '@angular/core';
import { Router } from '@angular/router';

import {
  AuthStore,
  ErrorAlertComponent,
  ListTitleComponent,
  PaginatorWithLoadMoreComponent,
  PERMISSIONS,
  PillWithCancelComponent,
  ViewMode,
} from '@sandbox-platform/shared-ui';

import {
  CommissionInvoiceFilterDisplayPill,
  CommissionInvoiceFilterDisplayStore,
  CommissionInvoiceListStore,
  CommissionInvoiceListStoreType,
} from './+state';
import { CommissionInvoiceDTO } from '../../../types/commission-invoice.types';
import { CommissionInvoiceFilterDialogComponent } from './components/commission-invoice-filter-dialog.component';
import { CommissionInvoiceListTableComponent } from './components/commission-invoice-list-table.component';
import { CommissionInvoiceSearchFilterComponent } from './components/commission-invoice-search-filter.component';

@Component({
  standalone: true,
  imports: [
    ErrorAlertComponent,
    ListTitleComponent,
    PaginatorWithLoadMoreComponent,
    PillWithCancelComponent,
    CommissionInvoiceFilterDialogComponent,
    CommissionInvoiceListTableComponent,
    CommissionInvoiceSearchFilterComponent,
  ],
  selector: 'lib-commission-invoice-list',
  templateUrl: './commission-invoice-list.component.html',
  host: { class: 'block h-full' },
})
export class CommissionInvoiceListComponent {
  protected readonly store: CommissionInvoiceListStoreType = inject(
    CommissionInvoiceListStore
  );
  protected readonly filterDisplayStore = inject(
    CommissionInvoiceFilterDisplayStore
  );
  protected readonly authStore = inject(AuthStore);
  private readonly router = inject(Router);

  protected readonly currentViewIsCard = computed(
    () => this.store.currentView() === 'card'
  );
  protected readonly canView = computed(() =>
    this.authStore.hasExpertPermission(
      PERMISSIONS.EXPERT.COMMISSION_INVOICE_READ
    )
  );

  constructor() {
    this.filterDisplayStore.syncAppliedFilters(this.store.filter());
    this.store.get();
  }

  onViewChange(view: ViewMode): void {
    this.store.setCurrentView(view);
  }

  onFromDateChange(event: Event): void {
    this.store.onFromDateChange(event);
  }

  onToDateChange(event: Event): void {
    this.store.onToDateChange(event);
  }

  onOpenFilterDialog(): void {
    this.filterDisplayStore.openDialog(this.store.filter());
  }

  onCloseFilterDialog(): void {
    this.filterDisplayStore.closeDialog();
  }

  onFiltersApply(): void {
    const filters = this.filterDisplayStore.applyDraft();
    this.store.setFilters(filters);
  }

  onLoadMore(): void {
    this.store.loadMore();
  }

  onSortChange(event: { sortBy: string; sortDirection: 'asc' | 'desc' }): void {
    this.store.setSort(event.sortBy, event.sortDirection);
  }

  onClearFilterPill(pill: CommissionInvoiceFilterDisplayPill): void {
    const filters = this.filterDisplayStore.clearAppliedFilter(pill);
    this.store.setFilters(filters);
  }

  onClearAllFilterPills(): void {
    const filters = this.filterDisplayStore.clearAllAppliedFilters();
    this.store.setFilters(filters);
  }

  onRefresh(): void {
    this.store.get();
  }

  handleViewInvoice(invoice: CommissionInvoiceDTO): void {
    this.router.navigate(['/commission-invoices', invoice.id]);
  }
}
